import { defineStore } from 'pinia'
import { useRestApi } from '../services/api'
import { FilePondFile } from 'filepond'
import { DriveoLocation, useConfigStore } from './config'
import { useDialogStore } from './dialog'
import { gtagCall } from '../services/gtm'
import { EVENT_BUS_EVENTS, useEventBus } from './eventBus'
import { ParseFullName } from '../services/form.service'
import { AppUploadFile } from '../CommonComponents/common/AppImageUpload.vue'
import { useAppScreen } from '../services/app-screen'

export interface CustomFilePondFile extends FilePondFile {
	options: {
		type: string
	}
}

export type LookupVinResponse = {
	data: ParseVinTrim[] | LookupVinError
	licensePlateData: LicensePlateTrim
	lookupAPI: 'tradePending' | 'galves'
}

export type LookupVinError = { error: string; code: string; blackout_year?: number }

export interface ParseVinTrim extends Trim {
	make: { name: string; external_id: string; [key: string]: string | Record<string, unknown> }
	model: { name: string; external_id: string }
	styles: Style[]
	vin: string
	year: string
}

export interface LicensePlateTrim {
	[key: string]: string | Record<string, unknown>
	color: { name: string; abbreviation: string }
}

export interface QuizItem {
	category: string
	denied: boolean
	description: string
	id: number
	key: string
	name: string
	required: boolean
	comment?: string
}

export interface Question {
	answer_type: string
	answers: string
	checkedQuestion?: string[]
	id: number
	options_answer: string
	question: string
	question_id: number
	type: string
	updated_at: string
	why_ask_that: string
}

export interface AnswerItem {
	id: number
	answer_options: string
}

export interface SellType {
	type: string
	comment?: string
}

export enum RateCondition {
	Clean = 1,
	Average,
	Rough,
	Damaged,
	NotSure,
}
export interface Trim {
	body_type: string
	doors: string
	drivetrain: string
	drivetrain_key: string
	engine: string
	trade_pending_id: string
	trim_name?: string
	licensePlate?: LicensePlateTrim
}
export interface Style {
	name: string
	external_id: string
}
export interface Model {
	name: string
	external_id: string
}
export interface Make {
	name: string
	external_id: string
}
export interface Office {
	id: number
	name: string
	address: string
	city: string
	state: string
	zip: string
	is_remote: boolean
	is_main: boolean
	published: boolean
	visible: boolean
}
export interface Vehicle {
	id: string
	year: string
	vin: string
	plate: string
	status: string
	make: { name: string; [key: string]: string | Record<string, unknown> }
	model: { name: string; [key: string]: string | Record<string, unknown> }
	appointments: Record<string, unknown>[]
	offer: Record<string, unknown>
	offers: Record<string, unknown>[]
	seller: Record<string, string>
	location: DriveoLocation

	[key: string]:
		| number
		| string
		| Record<string, unknown>
		| Record<string, unknown>[]
		| {
				[key: string]: string | Record<string, unknown>
		  }
}

export interface TabsEventObject {
	activeTabIndex: number
}

export enum Resets {
	// Contact = 'Contact',
	AboutYourRide = 'AboutYourRide',
	// Trims = 'Trims',
	Mileage = 'Mileage',
	Aftermarket = 'Aftermarket',
	RateCondition = 'RateCondition',
	StandardQuiz = 'StandardQuiz',
	DamagedQuiz = 'DamagedQuiz',
	MechanicalIssues = 'MechanicalIssues',
	CosmeticIssues = 'CosmeticIssues',
	SnapYourRide = 'SnapYourRide',
	WhatDoYouWant = 'WhatDoYouWant',
	SelectLocation = 'SelectLocation',
}
export enum Actions {
	SubmitContactForm = 'SubmitContactForm',
	SubmitAboutYourRideForm = 'SubmitAboutYourRideForm',
	SubmitAboutYourRideFormModal = 'SubmitAboutYourRideFormModal',
	SubmitTrimsForm = 'SubmitTrimsForm',
	SubmitTrimsFormModal = 'SubmitTrimsFormModal',
	SubmitMileageForm = 'SubmitMileageForm',
	SubmitAftermarketForm = 'SubmitAftermarketForm',
	SubmitRateConditionForm = 'SubmitRateConditionForm',
	SubmitStandardQuizForm = 'SubmitStandardQuizForm',
	SubmitDamagedQuizForm = 'SubmitDamagedQuizForm',
	SubmitMoreDamagedOptionsForm = 'SubmitMoreDamagedOptionsForm',
	SubmitMechanicalIssuesForm = 'SubmitMechanicalIssuesForm',
	SubmitCosmeticIssuesForm = 'SubmitCosmeticIssuesForm',
	SubmitSnapYourRideForm = 'SubmitSnapYourRideForm',
	SubmitWhatDoYouWant = 'SubmitWhatDoYouWant',
	SubmitSelectLocation = 'SubmitSelectLocation',
}
export enum BackActions {
	BackFromTrimsForm = 'BackFromTrimsForm',
	BackFromDamagedQuiz = 'BackFromDamagedQuiz',
	BackFromMechanicalIssues = 'BackFromMechanicalIssues',
	BackFromSnapYourRide = 'BackFromSnapYourRide',
	BackFromRejected = 'BackFromRejected',
	BackFromWhatDoYouWant = 'BackFromWhatDoYouWant',
	BackFromSelectLocation = 'BackFromSelectLocation',
	BackFromContactInfo = 'BackFromContactInfo',
}
export enum Validations {
	IsNameValid = 'IsNameValid',
	IsEmailValid = 'IsEmailValid',
	IsPhoneValid = 'IsPhoneValid',
	IsZIPValid = 'IsZIPValid',
	IsContactInfoValid = 'IsContactInfoValid',

	IsAboutYourRideValid = 'IsAboutYourRideValid',
	IsYMMValid = 'IsYMMValid',
	IsYearValid = 'IsYearValid',
	IsMakeValid = 'IsMakeValid',
	IsModelValid = 'IsModelValid',
	IsVinOrPlateValid = 'IsVinOrPlateValid',
	IsVinValid = 'IsVinValid',
	IsPlateValid = 'IsPlateValid',
	IsStateValid = 'IsStateValid',
	IsStylesLoaded = 'IsStylesLoaded',

	IsTrimSelected = 'IsTrimSelected',

	IsMileageValid = 'IsMileageValid',

	IsAftermarketValid = 'IsAftermarketValid',

	IsRateConditionValid = 'IsRateConditionValid',

	IsQuizValid = 'IsQuizValid',

	IsMoreDamagedOptionsValid = 'IsMoreDamagedOptionsValid',

	IsMechanicalIssuesValid = 'IsMechanicalIssuesValid',

	IsCosmeticIssuesValid = 'IsCosmeticIssuesValid',

	IsWhatDoYouWantValid = 'IsSecondWantsValid',
	IsOfficeSelected = 'IsOfficeSelected',
}
export enum Steps {
	ContactInfo,
	AboutYourRide,
	Trims,
	Mileage,
	Aftermarket,
	RateCondition,
	StandardQuiz,
	DamagedQuiz,
	MoreDamagedOptions,
	MechanicalIssues,
	CosmeticIssues,
	SnapYourRide,
	WhatDoYouWant,
	SelectLocation,
	Congrats,
	Rejected,
	NeedChat,
}
export type Step = {
	id: Steps
	title: string
	progress: number
	valid: Validations[] | boolean
	action: Actions
	back?: BackActions
	reset?: Resets
	terminate?: boolean
}
export type FormState = {
	step: number
	rejectedStep: Steps | undefined
	totalProgress: number
	steps: Step[]
	vinOrLPType: boolean

	name: string
	email: string
	phone: string
	zip: string

	vehicles: Vehicle[]

	year: string
	make: string
	model: string

	vin: string
	plate: string
	state: string

	makes: Make[]
	models: Model[]
	states: { name: string; label: string }[]

	styles: Style[]
	trims: Trim[]

	modalVehicleData: {
		year: string
		make: string
		model: string
		makes: Make[]
		models: Model[]
		vin: string
		plate: string
		state: string
		styles: Style[]
	}
	trimsModal: Trim[]
	trimModal: Trim

	trim: Trim

	mileage: string

	aftermarkets: boolean
	aftermarketsComment: string

	rateCondition: RateCondition | false

	quizList: QuizItem[]
	quizItems: QuizItem[]

	moreDamagedList: QuizItem[]
	moreDamagedItems: QuizItem[]

	mechIssueList: QuizItem[]
	mechIssueItems: QuizItem[]

	cosmIssueList: QuizItem[]
	cosmIssueItems: QuizItem[]

	images: AppUploadFile[]

	sellType: string
	ownType: string
	selectedOffice: number | undefined

	token: string

	soldMake: string
	firedGtagEvents: string[]
}

export const useFormStore = defineStore({
	id: 'formStore',
	state: () =>
		({
			step: 0,
			rejectedStep: undefined,
			totalProgress: 7,
			steps: [
				{
					id: Steps.AboutYourRide,
					title: 'What kind of ride do you have?',
					progress: 0,
					valid: [Validations.IsAboutYourRideValid],
					action: Actions.SubmitAboutYourRideForm,
					reset: Resets.AboutYourRide,
				},
				{
					id: Steps.Trims,
					title: 'Find your ride',
					progress: 1,
					valid: [Validations.IsTrimSelected],
					action: Actions.SubmitTrimsForm,
					back: BackActions.BackFromTrimsForm,
				},
				{
					id: Steps.Mileage,
					title: 'How many miles does your ride have?',
					progress: 2,
					valid: [Validations.IsMileageValid],
					action: Actions.SubmitMileageForm,
					reset: Resets.Mileage,
				},
				{
					id: Steps.Aftermarket,
					title: 'Any aftermarket modifications or parts?',
					progress: 3,
					valid: [Validations.IsAftermarketValid],
					action: Actions.SubmitAftermarketForm,
					reset: Resets.Aftermarket,
				},
				{
					id: Steps.RateCondition,
					title: 'Rate the condition of your ride',
					progress: 4,
					valid: [Validations.IsRateConditionValid],
					action: Actions.SubmitRateConditionForm,
					reset: Resets.RateCondition,
				},
				{
					id: Steps.StandardQuiz,
					title: "Uncheck any that aren't true",
					progress: 5,
					valid: [Validations.IsQuizValid],
					action: Actions.SubmitStandardQuizForm,
					reset: Resets.StandardQuiz,
				},
				{
					id: Steps.DamagedQuiz,
					title: 'Check any that are true',
					progress: 5,
					valid: [Validations.IsQuizValid],
					action: Actions.SubmitDamagedQuizForm,
					back: BackActions.BackFromDamagedQuiz,
					reset: Resets.DamagedQuiz,
				},
				{
					id: Steps.MoreDamagedOptions,
					title: 'Check all that apply',
					progress: 5.15,
					valid: [Validations.IsMoreDamagedOptionsValid],
					action: Actions.SubmitMoreDamagedOptionsForm,
				},
				{
					id: Steps.MechanicalIssues,
					title: 'Any mechanical issues?',
					progress: 5.3,
					valid: [Validations.IsMechanicalIssuesValid],
					action: Actions.SubmitMechanicalIssuesForm,
					back: BackActions.BackFromMechanicalIssues,
					reset: Resets.MechanicalIssues,
				},
				{
					id: Steps.CosmeticIssues,
					title: 'Any cosmetic issues?',
					progress: 5.5,
					valid: [Validations.IsCosmeticIssuesValid],
					action: Actions.SubmitCosmeticIssuesForm,
					reset: Resets.CosmeticIssues,
				},
				{
					id: Steps.SnapYourRide,
					title: 'Show us your ride (Optional)',
					progress: 6,
					valid: true,
					back: BackActions.BackFromSnapYourRide,
					action: Actions.SubmitSnapYourRideForm,
					reset: Resets.SnapYourRide,
				},
				{
					id: Steps.WhatDoYouWant,
					title: 'Selling or trading in?',
					progress: 6.4,
					valid: [Validations.IsWhatDoYouWantValid],
					back: BackActions.BackFromWhatDoYouWant,
					action: Actions.SubmitWhatDoYouWant,
					reset: Resets.WhatDoYouWant,
				},
				{
					id: Steps.SelectLocation,
					title: '',
					progress: 6.6,
					valid: [Validations.IsOfficeSelected],
					back: BackActions.BackFromSelectLocation,
					action: Actions.SubmitSelectLocation,
					reset: Resets.SelectLocation,
				},
				{
					id: Steps.ContactInfo,
					title: 'Your contact info for the quote',
					progress: 7,
					valid: [Validations.IsContactInfoValid],
					back: BackActions.BackFromContactInfo,
					action: Actions.SubmitContactForm,
				},
				{
					id: Steps.Congrats,
					progress: 7,
					title: 'Success! Here’s What Happens Next.',
					terminate: true,
				},
				{
					id: Steps.Rejected,
					title: "Uh-oh, your ride doesn't qualify for Driveo",
					back: BackActions.BackFromRejected,
				},
				{
					id: Steps.NeedChat,
					title: '',
					terminate: true,
				},
			],

			vinOrLPType: false,

			// contact info step
			name: '',
			email: '',
			phone: '',
			zip: '',

			// pick your ride step
			vehicles: [],

			// about your ride step
			year: 'Year',
			make: 'Make',
			model: 'Model',

			vin: '',
			plate: '',
			state: 'STATE:',

			makes: [],
			models: [],
			states: [
				{ name: 'STATE:', label: 'STATE:' },
				{ name: 'Alabama', label: 'AL' },
				{ name: 'Alaska', label: 'AK' },
				{ name: 'Arizona', label: 'AZ' },
				{ name: 'Arkansas', label: 'AR' },
				{ name: 'California', label: 'CA' },
				{ name: 'Colorado', label: 'CO' },
				{ name: 'Connecticut', label: 'CT' },
				{ name: 'Delaware', label: 'DE' },
				{ name: 'District of Columbia', label: 'DC' },
				{ name: 'Florida', label: 'FL' },
				{ name: 'Georgia', label: 'GA' },
				{ name: 'Hawaii', label: 'HI' },
				{ name: 'Idaho', label: 'ID' },
				{ name: 'Illinois', label: 'IL' },
				{ name: 'Indiana', label: 'IN' },
				{ name: 'Iowa', label: 'IA' },
				{ name: 'Kansas', label: 'KS' },
				{ name: 'Kentucky', label: 'KY' },
				{ name: 'Louisiana', label: 'LA' },
				{ name: 'Maine', label: 'ME' },
				{ name: 'Maryland', label: 'MD' },
				{ name: 'Massachusetts', label: 'MA' },
				{ name: 'Michigan', label: 'MI' },
				{ name: 'Minnesota', label: 'MN' },
				{ name: 'Mississippi', label: 'MS' },
				{ name: 'Missouri', label: 'MO' },
				{ name: 'Montana', label: 'MT' },
				{ name: 'Nebraska', label: 'NE' },
				{ name: 'Nevada', label: 'NV' },
				{ name: 'New Hampshire', label: 'NH' },
				{ name: 'New Jersey', label: 'NJ' },
				{ name: 'New Mexico', label: 'NM' },
				{ name: 'New York', label: 'NY' },
				{ name: 'North Carolina', label: 'NC' },
				{ name: 'North Dakota', label: 'ND' },
				{ name: 'Ohio', label: 'OH' },
				{ name: 'Oklahoma', label: 'OK' },
				{ name: 'Oregon', label: 'OR' },
				{ name: 'Pennsylvania', label: 'PA' },
				{ name: 'Rhode Island', label: 'RI' },
				{ name: 'South Carolina', label: 'SC' },
				{ name: 'South Dakota', label: 'SD' },
				{ name: 'Tennessee', label: 'TN' },
				{ name: 'Texas', label: 'TX' },
				{ name: 'Utah', label: 'UT' },
				{ name: 'Vermont', label: 'VT' },
				{ name: 'Virginia', label: 'VA' },
				{ name: 'Washington', label: 'WA' },
				{ name: 'West Virginia', label: 'WV' },
				{ name: 'Wisconsin', label: 'WI' },
				{ name: 'Wyoming', label: 'WY' },
			],
			styles: [],
			trims: [],
			trimsModal: [],

			modalVehicleData: {
				year: '',
				make: '',
				model: '',
				makes: [],
				models: [],
				vin: '',
				plate: '',
				state: '',
				styles: [],
			},

			trim: {} as Trim,
			trimModal: {} as Trim,

			mileage: '',

			aftermarkets: false,
			aftermarketsComment: '',

			rateCondition: false,

			quizList: [],
			quizItems: [],

			moreDamagedList: [],
			moreDamagedItems: [],

			mechIssueList: [],
			mechIssueItems: [],

			cosmIssueList: [],
			cosmIssueItems: [],

			images: [],

			sellType: '',
			ownType: '',
			selectedOffice: undefined,

			token: '',

			soldMake: '',
			firedGtagEvents: [],
		} as FormState),
	persist: {
		key: 'driveoWidgetStore',
	},

	getters: {
		// step getters
		hasNextStep: (state): boolean => {
			return state.step + 1 < state.steps.length
		},

		hasPrevStep: (state): boolean => {
			return state.step > 0
		},

		currentStepInd: (state): number => {
			return state.step
		},

		currentStep: (state): Step => {
			return state.steps[state.step]
		},

		progress: (state): number | false => {
			if (!state.steps[state.step].progress && state.steps[state.step].progress !== 0)
				return false
			const prog = (state.steps[state.step].progress * 100) / state.totalProgress
			return prog < 4 ? 4 : prog > 97 ? 97 : prog
		},

		// form getters
		filteredVehicles: (state): Vehicle[] => {
			return state.vehicles.filter(v => v.make)
		},

		getYears: (): (string | number)[] => {
			const { config } = useConfigStore()
			return (['Year'] as (string | number)[]).concat(config.years)
		},

		getMakes: (state): Make[] => {
			return [{ name: 'Make', external_id: 'Make' }].concat(state.makes)
		},

		getModels: (state): Make[] => {
			return [{ name: 'Model', external_id: 'Model' }].concat(state.models)
		},

		// validations
		[Validations.IsNameValid]: (state): boolean => {
			const { getConfigContent } = useConfigStore()
			return !!state.name && getConfigContent('fullName', false)
				? /^\w+(?:\s+\w+)+$/.test(state.name.trim())
				: state.name.length >= 2
		},

		[Validations.IsEmailValid]: (state): boolean => {
			return !!state.email && /.+@.+\..+/.test(state.email)
		},

		[Validations.IsPhoneValid]: (state): boolean => {
			return !!state.phone && /\(\d\d\d\) \d\d\d-\d\d\d\d/.test(state.phone)
		},

		[Validations.IsZIPValid]: (state): boolean => {
			return !!state.zip && /\d\d\d\d\d/.test(state.zip)
		},

		[Validations.IsContactInfoValid]: (): boolean => {
			const { getConfigContent } = useConfigStore()

			return (
				useFormStore()[Validations.IsNameValid] &&
				useFormStore()[Validations.IsEmailValid] &&
				useFormStore()[Validations.IsPhoneValid] &&
				((getConfigContent('zipRequired', false) &&
					useFormStore()[Validations.IsZIPValid]) ||
					!getConfigContent('zipRequired', false))
			)
		},

		[Validations.IsAboutYourRideValid]: (): boolean => {
			return (
				useFormStore()[Validations.IsYMMValid] ||
				useFormStore()[Validations.IsVinOrPlateValid]
			)
		},

		[Validations.IsYMMValid]: (): boolean => {
			return (
				useFormStore()[Validations.IsYearValid] &&
				useFormStore()[Validations.IsMakeValid] &&
				useFormStore()[Validations.IsModelValid] &&
				useFormStore()[Validations.IsStylesLoaded]
			)
		},

		[Validations.IsYearValid](state) {
			return !!state.year && state.year != 'Year'
		},

		[Validations.IsMakeValid](state) {
			return !!state.make && state.make != 'Make'
		},

		[Validations.IsModelValid](state) {
			return !!state.model && state.model != 'Model'
		},

		[Validations.IsVinOrPlateValid]: (): boolean => {
			return (
				useFormStore()[Validations.IsVinValid] ||
				(useFormStore()[Validations.IsPlateValid] &&
					useFormStore()[Validations.IsStateValid])
			)
		},

		[Validations.IsVinValid]: (state): boolean => {
			return !!state.vin && /[A-HJ-NPR-Za-hj-npr-z\d]{17}/.test(state.vin)
		},

		[Validations.IsPlateValid]: (state): boolean => {
			return !!state.plate && state.plate.length > 1
		},

		[Validations.IsStateValid]: (state): boolean => {
			return !!state.state && state.state !== 'STATE:'
		},

		[Validations.IsStylesLoaded]: (state): boolean => {
			return !!state.styles.length
		},

		[Validations.IsTrimSelected]: (state): boolean => {
			return !!state.trim.body_type
		},

		[Validations.IsMileageValid]: (state): boolean => {
			return !!state.mileage && state.mileage[0] !== '0'
		},

		[Validations.IsAftermarketValid]: (state): boolean => {
			return !state.aftermarkets || (!!state.aftermarkets && !!state.aftermarketsComment)
		},

		[Validations.IsRateConditionValid]: (state): boolean => {
			return !!state.rateCondition
		},

		[Validations.IsQuizValid]: (state): boolean => {
			return !!state.quizItems.length
		},

		[Validations.IsMoreDamagedOptionsValid]: (state): boolean => {
			return !!state.moreDamagedItems.length
		},

		[Validations.IsMechanicalIssuesValid]: (state): boolean => {
			return !!state.mechIssueList.length && !!state.mechIssueItems.length
		},

		[Validations.IsCosmeticIssuesValid]: (state): boolean => {
			return !!state.cosmIssueList.length && !!state.cosmIssueItems.length
		},

		[Validations.IsWhatDoYouWantValid]: (state): boolean => {
			return !!state.sellType
		},
		[Validations.IsOfficeSelected]: (state): boolean => {
			return !!state.selectedOffice || state.selectedOffice === undefined
		},
	},

	actions: {
		// step actions
		nextStep() {
			if (this.hasNextStep) {
				this.step++
			}
		},

		prevStep() {
			if (this.hasPrevStep) {
				this.step--
			}
		},

		setStep(stepId: Steps) {
			const nextStep = this.steps.findIndex(s => s.id === stepId)
			this.step = ~nextStep ? nextStep : this.step
		},

		resetForm(step: Steps) {
			let found = false
			this.steps.forEach(s => {
				if (found || s.id === step) {
					found = true
					//to do
					s.reset && this[s.reset] && this[s.reset]()
				}
			})
		},

		// form actions
		async [Actions.SubmitContactForm](): Promise<void> {
			const { config, getOffices } = useConfigStore()
			const eventBus = useEventBus()
			const { post, postForm, put } = useRestApi()

			const fullName = await ParseFullName(this.name, this.email)

			const makeID = this.getMakes.find(m => m.name === this.make)
			const modelID = this.getModels.find(m => m.name === this.model)

			const states: (number | { id: number; comment?: string })[] = this.quizItems.map(
				qi => qi.id,
			)

			states.push(...this.moreDamagedItems.map(md => md.id))
			states.push(...this.mechIssueItems.map(({ id, comment }) => ({ id, comment })))
			states.push(
				...this.cosmIssueItems.map(({ id, comment }) => ({
					id,
					comment,
				})),
			)

			const formData = {
				form_step: 'confirmation',
				finished: true,
				widget_token: config.token,

				utmData: {
					utm_medium: config.utm_medium,
					utm_source: config.utm_source,
					utm_campaign: config.utm_campaign,
					utm_term: config.utm_term,
				},

				email: this.email,
				form_type: 'widget',
				location_id: config.location?.id,
				name: this.name,
				phone: this.phone.replace(/[() -]/g, ''),
				first_name: fullName?.first_name != '0' ? fullName?.first_name : undefined,
				last_name: fullName?.last_name != '0' ? fullName?.last_name : undefined,
				zip: this.zip,

				ext_make_id: makeID?.external_id || this.make,
				ext_model_id: modelID?.external_id || this.model,

				make: this.make,
				model: this.model,

				plate: this.plate ? `${this.plate} ${this.state}` : '',
				vin: this.vin,
				year: this.year,

				style:
					this.trim?.body_type &&
					`${this.trim.body_type} ${this.trim.trim_name} ${this.trim.drivetrain_key}`,
				body_type: this.trim.body_type,
				drivetrain: this.trim.drivetrain,
				engine: this.trim.engine,
				ext_style_id: this.trim.trim_name,
				trade_pending_id: this.trim.trade_pending_id,
				trim_title: this.trim.trim_name,
				licence_plate: this.trim.licensePlate,

				mileage: this.mileage.replace(',', ''),

				aftermarket: this.aftermarkets,
				aftermarket_list: this.aftermarketsComment,

				sell_type: this.sellType,
				own_type: this.ownType,

				office_id:
					(this.sellType !== 'SELL' && this.selectedOffice) ||
					getOffices.find(office => office.is_main)?.id,

				condition_id: this.rateCondition,
				states,
			}

			const action = this.token
				? () => put<{ data: Record<string, string> }>(`vehicles/${this.token}`, formData)
				: () => post<{ data: Record<string, string> }>('vehicles/reverse', formData)

			return new Promise((resolve, reject) => {
				action()
					.then(data => {
						const event = 'lead'
						// check what event fires once for each lead
						if (!this.firedGtagEvents.includes(event)) {
							gtagCall('event', event)
							this.firedGtagEvents.push(event)
						}

						if (!this.images.length) return [data]

						const token = data.data.token
						const uploadImagePromises = this.images.map(img => {
							const form = new FormData()
							form.append('file_url', img.name)

							return postForm(`vehicles/${token}/images`, form)
						})

						return Promise.all(uploadImagePromises)
					})
					.then(() => {
						const make = this.make
						this.$reset()
						this.soldMake = make
						this.setStep(Steps.Congrats)
						resolve()
					})
					.catch(err => {
						console.log(err)
						eventBus.emit({
							name: EVENT_BUS_EVENTS.SUBMIT_FORM_ERROR,
							data: err.message || 'API error',
						})

						reject(err)
					})
			})
		},

		[Resets.AboutYourRide](): void {
			this.year = 'Year'
			this.make = 'Make'
			this.model = 'Model'
		},
		[Resets.SnapYourRide](): void {
			return
		},

		changeYear(value: string): Promise<void> {
			const { get } = useRestApi()
			const url = `lookup/makes?year=${value}&form_type=widget`

			this.make = 'Make'
			this.makes = []
			this.model = 'Model'
			this.vin = ''
			this.plate = ''
			this.styles = []
			this.trims = []
			this.trim = {} as Trim
			this.setStep(Steps.AboutYourRide)

			return new Promise((resolve, reject) => {
				get(url)
					.then(data => {
						if (data.data) {
							this.makes = data.data as Make[]
						}
						resolve()
					})
					.catch(reject)
			})
		},

		changeMake(value: string): Promise<void> {
			const { get } = useRestApi()
			const url = `lookup/models?year=${this.year}&make_id=${value}&form_type=widget`

			this.model = 'Model'
			this.models = []
			this.vin = ''
			this.plate = ''
			this.styles = []
			this.trims = []
			this.trim = {} as Trim
			this.setStep(Steps.AboutYourRide)

			return new Promise((resolve, reject) => {
				get(url)
					.then(data => {
						if (data.data) {
							this.models = data.data as Model[]
						}
						resolve()
					})
					.catch(reject)
			})
		},

		changeModel(value: string): Promise<void> {
			const { get } = useRestApi()
			const url = `lookup/styles?year=${this.year}&make_id=${this.make}&model_id=${value}&form_type=widget`

			this.vin = ''
			this.plate = ''
			this.styles = []
			this.trims = []
			this.trim = {} as Trim
			this.setStep(Steps.AboutYourRide)

			return new Promise((resolve, reject) => {
				get(url)
					.then(data => {
						if (data.data) {
							this.styles = data.data as Style[]
						}
						resolve()
					})
					.catch(reject)
			})
		},

		[Actions.SubmitAboutYourRideForm](): Promise<void> {
			const dialogs = useDialogStore()

			if (this.trims.length && !dialogs.headerFormModalOpen) {
				this.nextStep()
				return Promise.resolve()
			}

			const { get } = useRestApi()

			return new Promise((resolve, reject) => {
				if (this[Validations.IsYMMValid]) {
					const promises = this.styles.map(style => {
						return get<{ data: Trim[] }>(
							`lookup/equipments?year=${this.year}&make_id=${this.make}&model_id=${
								this.model
							}&style_id=${encodeURIComponent(style.external_id)}`,
						)
					})

					Promise.all(promises)
						.then(data => {
							this.trims = data.reduce((acc, curr, index) => {
								return acc.concat(
									curr.data.map(trim => ({
										...trim,
										trim_name: this.styles[index].external_id,
									})) as [],
								)
							}, [])

							if (this.trims?.length === 1) {
								this.trim = this.trims[0]
							}
							this.nextStep()
							resolve()
						})
						.catch(reject)
					return
				}

				if (this[Validations.IsVinValid]) {
					const url = `lookup/vin?vin=${this.vin}`

					this.vinOrLPType = true

					get<LookupVinResponse>(url)
						.then(data => {
							const errorCode = (data.data as LookupVinError).code
							if (errorCode === 'car_is_older_widget') {
								dialogs.openErrorModal({
									type: 'vin',
									title: 'Uh-Oh. Looks like your ride is older than 2004',
									subtitle:
										'Unfortunately we are not able to appraise cars older than 2004',
									isOld: true,
								})
								reject('car_is_older_widget')
								return
							}

							if (errorCode === 'forbidden_make') {
								reject(data.data)
								return
							}

							const formData = (data.data as ParseVinTrim[])[0]
							if (!formData) {
								dialogs.openErrorModal({
									type: 'vin',
									title: 'Uh-Oh, We Can’t Decode This VIN Number',
									subtitle:
										'Please try again or enter your license plate instead',
								})
								reject('VIN not found')
								return
							}

							this.year = formData.year
							this.makes = [
								{
									name: formData.make.name,
									external_id: formData.make.external_id,
								},
							]
							this.make = formData.make.name
							this.models = [
								{
									name: formData.model.name,
									external_id: formData.model.external_id,
								},
							]
							this.model = formData.model.name
							this.vin = formData.vin
							this.styles = formData.styles

							this.trims = (data.data as ParseVinTrim[]).reduce(
								(acc, curr) =>
									acc.concat(
										curr.styles.map(s => ({
											drivetrain_key: curr.drivetrain_key,
											drivetrain: curr.drivetrain,
											doors: curr.doors,
											body_type: curr.body_type,
											engine: curr.engine,
											trade_pending_id: curr.trade_pending_id,
											trim_name: s.external_id,
											licensePlate: data.licensePlateData,
										})),
									),
								[] as Trim[],
							)

							if (this.trims?.length === 1) {
								this.trim = this.trims[0]
							}

							this.nextStep()

							resolve()
						})
						.catch(reject)
					return
				}

				if (this[Validations.IsPlateValid]) {
					const url = `lookup/plate?plate=${this.plate}&state=${this.state}`

					this.vinOrLPType = true

					get<LookupVinResponse>(url)
						.then(data => {
							const errorCode = (data.data as LookupVinError).code
							if (errorCode === 'car_is_older_widget') {
								dialogs.openErrorModal({
									type: 'lp',
									title: 'Uh-Oh. Looks like your ride is older than 2004',
									subtitle:
										'Unfortunately we are not able to appraise cars older than 2004',
									isOld: true,
								})
								reject('car_is_older_widget')
								return
							}

							const formData = (data.data as ParseVinTrim[])[0]
							if (!formData) {
								dialogs.openErrorModal({
									type: 'lp',
									title: 'Uh-Oh, We Can’t Decode This License Plate',
									subtitle: 'Please try again or enter your VIN number instead',
								})
								reject('cannot_decode_plate')
								return
							}

							this.year = formData.year
							this.make = formData.make.name
							this.model = formData.model.name
							this.vin = formData.vin
							this.styles = formData.styles

							this.trims = (data.data as ParseVinTrim[]).reduce(
								(acc, curr) =>
									acc.concat(
										curr.styles.map(s => ({
											drivetrain_key: curr.drivetrain_key,
											drivetrain: curr.drivetrain,
											doors: curr.doors,
											body_type: curr.body_type,
											engine: curr.engine,
											trade_pending_id: curr.trade_pending_id,
											trim_name: s.external_id,
											licensePlate: data.licensePlateData,
										})),
									),
								[] as Trim[],
							)

							if (this.trims?.length === 1) {
								this.trim = this.trims[0]
							}

							this.nextStep()
							resolve()
						})
						.catch(reject)
					return
				}
			})
		},

		[Actions.SubmitAboutYourRideFormModal](): Promise<void> {
			const dialogs = useDialogStore()
			const form = useFormStore()
			const { get } = useRestApi()

			return new Promise((resolve, reject) => {
				if (this[Validations.IsVinValid]) {
					const url = `lookup/vin?vin=${this.vin}`

					get<LookupVinResponse>(url)
						.then(data => {
							const errorCode = (data.data as LookupVinError).code
							if (errorCode === 'car_is_older_widget') {
								dialogs.openErrorModal({
									open: false,
									type: 'vin',
									title: 'Uh-Oh. Looks like your ride is older than 2004',
									subtitle:
										'Unfortunately we are not able to appraise cars older than 2004',
									isOld: true,
								})
								dialogs.toggleFormModalError(true)
								reject('car_is_older_widget')
								return
							}

							const formData = (data.data as ParseVinTrim[])[0]
							if (!formData) {
								dialogs.openErrorModal({
									open: false,
									type: 'vin',
									title: 'Uh-Oh, We Can’t Decode This VIN Number',
									subtitle:
										'Please try again or enter your license plate instead',
								})
								dialogs.toggleFormModalError(true)
								reject('VIN not found')
								return
							}

							this.modalVehicleData.year = formData.year
							this.modalVehicleData.makes = [
								{
									name: formData.make.name,
									external_id: formData.make.external_id,
								},
							]
							this.modalVehicleData.make = formData.make.name
							this.modalVehicleData.models = [
								{
									name: formData.model.name,
									external_id: formData.model.external_id,
								},
							]
							this.modalVehicleData.model = formData.model.name
							this.modalVehicleData.vin = formData.vin
							this.modalVehicleData.styles = formData.styles

							this.trimsModal = (data.data as ParseVinTrim[]).reduce(
								(acc, curr) =>
									acc.concat(
										curr.styles.map(s => ({
											drivetrain_key: curr.drivetrain_key,
											drivetrain: curr.drivetrain,
											doors: curr.doors,
											body_type: curr.body_type,
											engine: curr.engine,
											trade_pending_id: curr.trade_pending_id,
											trim_name: s.external_id,
											licensePlate: data.licensePlateData,
										})),
									),
								[] as Trim[],
							)

							if (form.step === 1) {
								this.trims = this.trimsModal
								this.year = this.modalVehicleData.year + ''
								this.makes = [
									{
										name: formData.make.name,
										external_id: formData.make.external_id,
									},
								]
								this.make = this.modalVehicleData.make
								this.models = [
									{
										name: formData.model.name,
										external_id: formData.model.external_id,
									},
								]
								this.model = this.modalVehicleData.model
								this.vin = this.modalVehicleData.vin || ''
								this.plate = this.modalVehicleData.plate
								form.vinOrLPType = true

								if (this.trims?.length === 1) {
									this.trim = this.trims[0]
									this.trimModal = this.trims[0]
								}
								dialogs.closeTrimsModal()
							}

							if (this.trimsModal?.length === 1) {
								this.trimModal = this.trimsModal[0]
							}

							if (this.trimsModal?.length === 1) {
								// this.trimModal = this.trimsModal[0]
								// this.trim = this.trimsModal[0]
								// this.trim = this.trimModal
								// this.trims = this.trimsModal
								// this.year = this.modalVehicleData.year
								// this.make = this.modalVehicleData.make
								// this.makes = this.modalVehicleData.makes
								// this.models = this.modalVehicleData.models
								// this.model = this.modalVehicleData.model
								// this.vin = this.modalVehicleData.vin
								// this.plate = this.modalVehicleData.plate
								// this.state = this.modalVehicleData.state
								// this.styles = this.modalVehicleData.styles
								// this.vinOrLPType = true
								// dialogs.closeTrimsModal()
							}
							resolve()
						})
						.catch(reject)
					return
				}

				if (this[Validations.IsPlateValid]) {
					const url = `lookup/plate?plate=${this.plate}&state=${this.state}`

					get<LookupVinResponse>(url)
						.then(data => {
							const errorCode = (data.data as LookupVinError).code
							if (errorCode === 'car_is_older_widget') {
								dialogs.openErrorModal({
									open: false,
									type: 'lp',
									title: 'Uh-Oh. Looks like your ride is older than 2004',
									subtitle:
										'Unfortunately we are not able to appraise cars older than 2004',
									isOld: true,
								})
								dialogs.toggleFormModalError(true)
								reject('car_is_older_widget')
								return
							}

							const formData = (data.data as ParseVinTrim[])[0]
							if (!formData) {
								dialogs.openErrorModal({
									open: false,
									type: 'lp',
									title: 'Uh-Oh, We Can’t Decode This License Plate',
									subtitle: 'Please try again or enter your VIN number instead',
								})
								dialogs.toggleFormModalError(true)
								reject('cannot_decode_plate')
								return
							}

							this.modalVehicleData.year = formData.year
							this.modalVehicleData.make = formData.make.name
							this.modalVehicleData.makes = [
								{
									name: formData.make.name,
									external_id: formData.make.external_id,
								},
							]
							this.modalVehicleData.model = formData.model.name
							this.modalVehicleData.models = [
								{
									name: formData.model.name,
									external_id: formData.model.external_id,
								},
							]
							this.modalVehicleData.vin = formData.vin
							this.modalVehicleData.styles = formData.styles

							this.trimsModal = (data.data as ParseVinTrim[]).reduce(
								(acc, curr) =>
									acc.concat(
										curr.styles.map(s => ({
											drivetrain_key: curr.drivetrain_key,
											drivetrain: curr.drivetrain,
											doors: curr.doors,
											body_type: curr.body_type,
											engine: curr.engine,
											trade_pending_id: curr.trade_pending_id,
											trim_name: s.external_id,
											licensePlate: data.licensePlateData,
										})),
									),
								[] as Trim[],
							)

							if (form.step === 1) {
								this.trims = this.trimsModal
								this.year = this.modalVehicleData.year + ''
								this.makes = [
									{
										name: formData.make.name,
										external_id: formData.make.external_id,
									},
								]
								this.make = this.modalVehicleData.make
								this.models = [
									{
										name: formData.model.name,
										external_id: formData.model.external_id,
									},
								]
								this.model = this.modalVehicleData.model
								this.vin = this.modalVehicleData.vin || ''
								this.plate = this.modalVehicleData.plate
								form.vinOrLPType = true

								if (this.trims?.length === 1) {
									this.trim = this.trims[0]
									this.trimModal = this.trims[0]
								}
								dialogs.closeTrimsModal()
							}

							if (this.trimsModal?.length === 1) {
								this.trimModal = this.trimsModal[0]
							}
							resolve()
						})
						.catch(reject)
					return
				}
			})
		},

		[BackActions.BackFromTrimsForm](): void {
			this.prevStep()
		},

		[Actions.SubmitTrimsForm](): Promise<void> {
			this.nextStep()
			return Promise.resolve()
		},

		[Actions.SubmitTrimsFormModal](): Promise<void> {
			const form = useFormStore()
			if (form.step === Steps.AboutYourRide) {
				this.nextStep()
			}
			return Promise.resolve()
		},

		[Actions.SubmitMileageForm](): Promise<void> {
			this.nextStep()
			return Promise.resolve()
		},

		[Resets.Mileage](): void {
			this.mileage = ''
		},

		[Actions.SubmitAftermarketForm](): Promise<void> {
			this.nextStep()
			return Promise.resolve()
		},

		[Resets.Aftermarket](): void {
			this.aftermarkets = false
		},

		[Resets.SelectLocation](): void {
			this.selectedOffice = undefined
		},

		[Actions.SubmitRateConditionForm](): Promise<void> {
			const api = useRestApi()

			switch (this.rateCondition) {
				case RateCondition.Clean:
				case RateCondition.Average:
				case RateCondition.Rough:
				case RateCondition.NotSure: {
					return new Promise((resolve, reject) => {
						const url =
							this.rateCondition === RateCondition.Clean
								? '/lookup/state/clean-quiz'
								: '/lookup/state/standard-quiz'

						api.get<{ data: QuizItem[] }>(url)
							.then(data => {
								this.quizList = data.data
								this.quizItems = [...data.data]
								this.setStep(Steps.StandardQuiz)
								resolve()
							})
							.catch(reject)
					})
				}
				default: {
					return new Promise((resolve, reject) => {
						api.get<{ data: QuizItem[] }>('/lookup/state/damaged-quiz')
							.then(data => {
								this.quizList = data.data
								this.quizItems = []
								this.setStep(Steps.DamagedQuiz)
								resolve()
							})
							.catch(reject)
					})
				}
			}
		},

		[Resets.RateCondition](): void {
			this.aftermarkets = false
			this.aftermarketsComment = ''
		},

		[Actions.SubmitStandardQuizForm](): Promise<void> {
			const api = useRestApi()
			const { isMobile } = useAppScreen()

			if (
				this.quizList.length === this.quizItems.length &&
				this.rateCondition === RateCondition.Clean
			) {
				if (!isMobile()) {
					this.setStep(Steps.WhatDoYouWant)
				} else {
					this.setStep(Steps.SnapYourRide)
				}

				return Promise.resolve()
			}

			const requiredQuiz = this.quizList.filter(s => s.required)
			if (requiredQuiz.some(rq => !this.quizItems.some(qz => qz.id === rq.id))) {
				this.rejectedStep = Steps.StandardQuiz
				this.setStep(Steps.Rejected)

				return Promise.resolve()
			}

			if (this[Validations.IsMechanicalIssuesValid]) {
				this.setStep(Steps.MechanicalIssues)
				return Promise.resolve()
			}

			return new Promise((resolve, reject) => {
				api.get<{ data: QuizItem[] }>('/lookup/state/mechanical-issues')
					.then(data => {
						this.mechIssueList = data.data
						const noneItem = data.data?.find(mi => mi.key === 'None')
						this.mechIssueItems = noneItem ? [{ ...noneItem }] : []
						this.setStep(Steps.MechanicalIssues)
						resolve()
					})
					.catch(reject)
			})
		},

		[Resets.StandardQuiz](): void {
			this.quizList = []
			this.quizItems = []
		},

		[Actions.SubmitDamagedQuizForm](): Promise<void> {
			const api = useRestApi()

			if (this.quizItems.some(qi => qi.denied)) {
				this.rejectedStep = Steps.DamagedQuiz
				this.setStep(Steps.Rejected)

				return Promise.resolve()
			}

			return new Promise((resolve, reject) => {
				api.get<{ data: QuizItem[] }>('/lookup/state/more-damaged-options')
					.then(data => {
						this.moreDamagedList = data.data
						this.moreDamagedItems = []
						this.setStep(Steps.MoreDamagedOptions)
						resolve()
					})
					.catch(reject)
			})
		},

		[BackActions.BackFromDamagedQuiz](): void {
			this.setStep(Steps.RateCondition)
		},

		[Resets.DamagedQuiz](): void {
			this.moreDamagedList = []
			this.moreDamagedItems = []
		},

		[Actions.SubmitMoreDamagedOptionsForm](): Promise<void> {
			this.setStep(Steps.NeedChat)
			return Promise.resolve()
		},

		[Actions.SubmitMechanicalIssuesForm](): Promise<void> {
			const api = useRestApi()

			if (this[Validations.IsCosmeticIssuesValid]) {
				this.setStep(Steps.CosmeticIssues)
				return Promise.resolve()
			}

			return new Promise((resolve, reject) => {
				api.get<{ data: QuizItem[] }>('/lookup/state/cosmetic-issues')
					.then(data => {
						this.cosmIssueList = data.data
						const noneItem = data.data?.find(ci => ci.key === 'None')
						this.cosmIssueItems = noneItem ? [{ ...noneItem }] : []
						this.setStep(Steps.CosmeticIssues)
						resolve()
					})
					.catch(reject)
			})
		},

		[BackActions.BackFromMechanicalIssues](): void {
			switch (this.rateCondition) {
				case RateCondition.Clean:
				case RateCondition.Average:
				case RateCondition.Rough:
				case RateCondition.NotSure: {
					this.setStep(Steps.StandardQuiz)
					break
				}
				default:
					this.setStep(Steps.DamagedQuiz)
			}
		},

		[Resets.MechanicalIssues](): void {
			this.mechIssueList = []
			this.mechIssueItems = []
		},

		[Actions.SubmitCosmeticIssuesForm](): Promise<void> {
			const { isMobile } = useAppScreen()

			if (!isMobile()) {
				this.setStep(Steps.WhatDoYouWant)
			} else {
				this.setStep(Steps.SnapYourRide)
			}

			return Promise.resolve()
		},

		[Resets.CosmeticIssues](): void {
			this.images = []
			this.cosmIssueList = []
			this.cosmIssueItems = []
		},

		[Actions.SubmitSnapYourRideForm](): Promise<void> {
			this.nextStep()
			return Promise.resolve()
		},

		[BackActions.BackFromSnapYourRide](): void {
			if (
				this.rateCondition === RateCondition.Average ||
				this.rateCondition === RateCondition.Rough ||
				(this.rateCondition === RateCondition.Clean &&
					this.quizList.length !== this.quizItems.length)
			) {
				this.setStep(Steps.CosmeticIssues)
				return
			}
			this[BackActions.BackFromMechanicalIssues]()
		},

		[BackActions.BackFromRejected](): void {
			if (this.rejectedStep) {
				this.setStep(this.rejectedStep)
			} else {
				const dialogs = useDialogStore()

				dialogs.vehicleOpen = false
				this.$reset()
			}
		},

		[Actions.SubmitWhatDoYouWant](): Promise<void> {
			const { getOffices } = useConfigStore()

			if (this.sellType === 'TRADE' && getOffices?.length > 1) {
				this.setStep(Steps.SelectLocation)
			} else {
				this.setStep(Steps.ContactInfo)
			}
			return Promise.resolve()
		},

		[Actions.SubmitSelectLocation](): Promise<void> {
			this.setStep(Steps.ContactInfo)
			return Promise.resolve()
		},

		[BackActions.BackFromWhatDoYouWant](): void {
			const { isMobile } = useAppScreen()

			if (!isMobile()) {
				this[BackActions.BackFromSnapYourRide]()
			} else {
				this.setStep(Steps.SnapYourRide)
			}
		},

		[BackActions.BackFromSelectLocation](): void {
			this.setStep(Steps.WhatDoYouWant)
		},

		[BackActions.BackFromContactInfo](): void {
			const { getOffices } = useConfigStore()

			this.sellType === 'TRADE' && getOffices?.length > 1
				? this.setStep(Steps.SelectLocation)
				: this.setStep(Steps.WhatDoYouWant)
		},

		[Resets.WhatDoYouWant](): void {
			this.sellType = ''
		},
	},
})
